import { OrgAdminApiError, OrgAdminUnauthorizedError } from '@/api/orgAdmin';
import { authFetch } from './authFetch';
import type { Paginated } from '@/api/console';
import { useSessionStore } from '@/store/sessionStore';
import type { KeycloakAccountRow } from '@/pages/Console/Accounts/types';

/**
 * EAA-1820: fetch module for `GET /api/v1/accounts` (EAA-1829) — the read
 * side of the console accounts screen. `@/api/console.ts` already ships
 * `issueAccount` / `resetAccountPassword` / `deleteAccount` (the write side);
 * this file adds ONLY the list read.
 *
 * EAA-1804: this used to live at `pages/console/accounts/accountsApi.ts`,
 * kept outside `src/api/*` only because that ownership boundary was held by
 * another agent during EAA-1820. That boundary has lifted — this module now
 * sits alongside its siblings (`@/api/console.ts`, `@/api/orgAdmin.ts`) where
 * the data-layer convention (`.claude/rules/new-app-scaffold.mdc` §5a) always
 * intended it. It has not been merged into `@/api/console.ts` in this pass —
 * that would be a content change beyond a folder-shape sweep.
 *
 * Mirrors `@/api/console.ts`'s own `request()` byte-for-byte (envelope
 * unwrap, 401 → `OrgAdminUnauthorizedError`, `X-Org-Id` passthrough) rather
 * than importing it, since that helper is not exported.
 */

interface ApiEnvelope<T> {
    success: boolean;
    code: number;
    message?: string;
    description?: string;
    data: T;
}

async function request<T>(apiBase: string, token: string, path: string): Promise<T> {
    const orgId = useSessionStore.getState().actingOrgId;
    // EAA-1825: `authFetch` retries once with a renewed token on 401.
    const res = await authFetch(`${apiBase}${path}`, token, {
        method: 'GET',
        headers: { ...(orgId ? { 'X-Org-Id': orgId } : {}) },
    });
    if (res.status === 401) {
        throw new OrgAdminUnauthorizedError();
    }
    const envelope = (await res.json().catch(() => null)) as ApiEnvelope<T> | null;
    if (!res.ok || !envelope?.success) {
        throw new OrgAdminApiError(
            envelope?.description ?? `Request failed (${res.status})`,
            envelope?.code ?? res.status,
            (envelope as { data?: unknown } | null)?.data
        );
    }
    return envelope.data;
}

export interface FetchAccountsParams {
    page?: number;
    page_size?: number;
    search?: string;
    /** EAA-1820: real server-side filters `AccountsView.get` now accepts — see
     *  `useRealmAccountsAdapter.ts` for how the shared screen's facets/status
     *  chips map onto these. */
    status?: 'active' | 'pending' | 'locked';
    permission?: 'granted' | 'none';
    organization_id?: string;
    source?: 'console' | 'unknown';
    /** EAA-2068: application code(s) the account can reach. Several codes OR
     *  together and go out comma-separated (`app=lms,ach`); omitted = no app
     *  filter, exactly as before. */
    app?: string | string[];
    /** `username` | `email` | `created` | `status`, optionally `-` prefixed. */
    ordering?: string;
}

/**
 * EAA-1820: `GET /api/v1/accounts/facets` — the realm-wide facet counts that
 * replaced `useAccountsScan`'s capped client-side walk. `saved_filters` backs
 * the four constant "Bộ lọc đã lưu" chips (`no_permission` → `permission=none`,
 * `never_logged_in` → `status=pending` — see this endpoint's own `account_status`
 * logic, which computes both from the same `required_actions` check, so the two
 * numbers are never inconsistent with each other).
 */
export interface AccountsFacets {
    total: number;
    status: { active: number; pending: number; locked: number };
    permission: { granted: number; none: number };
    source: { console: number; unknown: number };
    organizations: { organization_id: string; organization_name: string; count: number }[];
    /** EAA-2068: accounts per application — enabled apps with at least one
     *  matching account, sorted by count desc then name. Counted over the
     *  search only, like every other group. Absent on a server that predates the
     *  app filter, so read it through `normalizeAppFacet`. */
    apps?: AccountsAppFacetEntry[];
    saved_filters: { all: number; no_permission: number; never_logged_in: number; locked: number };
    /** True when the facet counts themselves are a prefix, not the whole realm —
     *  see `AccountsPage`'s docstring for how this is surfaced honestly. */
    capped: boolean;
}

/** EAA-2068: one entry of the facets' `apps` group. */
export interface AccountsAppFacetEntry {
    app_code: string;
    app_name: string;
    count: number;
}

/** EAA-2068: the `apps` facet group, tolerating its absence (older server) and
 *  dropping entries without a code. */
export function normalizeAppFacet(raw: unknown): AccountsAppFacetEntry[] {
    if (!Array.isArray(raw)) return [];
    return (raw as Partial<AccountsAppFacetEntry>[])
        .filter((entry) => entry && typeof entry.app_code === 'string' && entry.app_code)
        .map((entry) => ({
            app_code: entry.app_code as string,
            app_name: entry.app_name || (entry.app_code as string),
            count: typeof entry.count === 'number' ? entry.count : 0,
        }));
}

/** EAA-2068: `app=<code>[,<code>]`, or nothing when no code is given. */
function appParam(app: string | string[] | undefined): string | null {
    const codes = (Array.isArray(app) ? app : app ? [app] : []).map((c) => c.trim()).filter(Boolean);
    return codes.length ? codes.join(',') : null;
}

export function fetchAccountFacets(
    apiBase: string,
    token: string,
    search?: string,
    /** EAA-2068: sent only for a LOCKED app scope (the app-detail screen). The
     *  server's facets currently count over `search` only and ignore `app`, so
     *  this is forward-compatible, not yet effective — see the EAA-2068 report.
     *  The realm screen's own app pick never narrows the counts, same as every
     *  other rail selection. */
    options: { app?: string | string[] } = {}
): Promise<AccountsFacets> {
    const qs = new URLSearchParams();
    if (search?.trim()) qs.set('search', search.trim());
    const app = appParam(options.app);
    if (app) qs.set('app', app);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return request<AccountsFacets>(apiBase, token, `/api/v1/accounts/facets${suffix}`);
}

/**
 * `GET /api/v1/accounts` — paginated Keycloak users read (EAA-1829). Gated
 * server-side to `IsSuperAdmin`; this is realm-wide, NOT org-scoped — see
 * `account_views.py::AccountsView.get`. Never returns a credential field.
 */
/**
 * EAA-1820: `GET /accounts` returns two fields beyond `Paginated<T>` that
 * describe the last-login column as a whole rather than any one row.
 * `last_login_available: false` means the realm records no login events, so
 * every row's null `last_login` is UNKNOWN — not "never signed in".
 */
export interface AccountsPage extends Paginated<KeycloakAccountRow> {
    last_login_available?: boolean;
    last_login_window_days?: number;
    /** Mirrors `AccountsFacets.capped` — true when the count itself is a
     *  prefix, not the whole realm. */
    capped?: boolean;
}

/**
 * EAA-1820 (frame 67): `GET /api/v1/accounts/<sub>` — the account profile.
 *
 * Distinct from a list row on purpose: EVERY active placement (a person can sit
 * in several đơn vị) and roles GROUPED BY APPLICATION, which a table cell has no
 * room for and which flat keys cannot express — two apps sharing a role name
 * would be indistinguishable.
 */
export interface AccountPlacement {
    organization_id: string;
    organization_name: string;
    level_name: string;
    role_key: string;
}

export interface AccountAppAccess {
    application: string;
    application_name: string;
    roles: string[];
}

export interface AccountDetail extends KeycloakAccountRow {
    first_name?: string;
    last_name?: string;
    placements: AccountPlacement[];
    app_access: AccountAppAccess[];
    last_login_available?: boolean;
}

export function fetchAccount(
    apiBase: string,
    token: string,
    sub: string
): Promise<AccountDetail> {
    return request<AccountDetail>(apiBase, token, `/api/v1/accounts/${encodeURIComponent(sub)}`);
}

/** EAA-1829: one `{sub, username}` per account matching the list filters, unpaged. */
export interface AccountSubsResult {
    results: { sub: string; username: string }[];
    count: number;
    capped: boolean;
}

/**
 * EAA-1829: `GET /api/v1/accounts/subs` — every matching sub in one cheap request,
 * served from the server's cached realm snapshot. Backs "Chọn tất cả N dòng khớp bộ
 * lọc" on the realm accounts screen; walking the list endpoint 100 rows at a time,
 * as the org screen does, would be 47 requests for a 4,700-account realm.
 */
export function fetchAccountSubs(
    apiBase: string,
    token: string,
    params: Omit<FetchAccountsParams, 'page' | 'page_size' | 'ordering'> = {}
): Promise<AccountSubsResult> {
    const qs = new URLSearchParams();
    if (params.search?.trim()) qs.set('search', params.search.trim());
    if (params.status) qs.set('status', params.status);
    if (params.permission) qs.set('permission', params.permission);
    if (params.organization_id) qs.set('organization_id', params.organization_id);
    if (params.source) qs.set('source', params.source);
    const subsApp = appParam(params.app);
    if (subsApp) qs.set('app', subsApp);
    const suffix = qs.toString();
    return request<AccountSubsResult>(apiBase, token, `/api/v1/accounts/subs${suffix ? `?${suffix}` : ''}`);
}

export function fetchAccounts(
    apiBase: string,
    token: string,
    params: FetchAccountsParams = {}
): Promise<AccountsPage> {
    const qs = new URLSearchParams();
    qs.set('page', String(params.page ?? 1));
    qs.set('page_size', String(params.page_size ?? 100));
    if (params.search?.trim()) {
        qs.set('search', params.search.trim());
    }
    if (params.status) qs.set('status', params.status);
    if (params.permission) qs.set('permission', params.permission);
    if (params.organization_id) qs.set('organization_id', params.organization_id);
    if (params.source) qs.set('source', params.source);
    const app = appParam(params.app);
    if (app) qs.set('app', app);
    if (params.ordering) qs.set('ordering', params.ordering);
    return request<AccountsPage>(apiBase, token, `/api/v1/accounts?${qs.toString()}`);
}
